import React, { useCallback, useEffect, useRef, useState } from "react";
import { Container, Card, CardBody, Form, Row, Col, Label, Input, InputGroup, Button } from "reactstrap";
import { Link, useLocation } from "react-router-dom";
import Barcode from "react-barcode";

import { getOrderPicking, orderPickingSort } from "services/api";
import { MySpinner } from "components/MySpinner";
import useQuery from "utils/useQuery";

import "./orderPicking.scss";

import { getMercureHubUrl, subscribeMercureReconnect } from "services/apiMercure";
import {
  playAudioArrayBuffer,
  ORDER_PICKING_VOICE_MERCURE_TOPIC,
  mimeFromVoiceFormatForRelay,
  VOICE_ORDER_PICKING_SKIP_RELAY_EVENT,
} from "services/apiVoice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWindowClose,
  faBox,
  faGripVertical,
  faVolumeUp,
} from "@fortawesome/free-solid-svg-icons";
import { VoiceTestModal } from "features/OrderPicking/VoiceTestModal/VoiceTestModal";
import Moment from "react-moment";


const MERCURE_TOPIC_ORDER_PICKING = "orderPicking";

const COLUMN_TITLES = ["В ожидании", "На сборку", "В работе"];
const COLUMN_KEYS = ["pending", "onAssembly", "inProgress"];

export const OrdersPickingPage = () => {
  const query = useQuery(useLocation);
  const [orderPickingColumns, setOrderPickingColumns] = useState({
    pending: [],
    onAssembly: [],
    inProgress: [],
  });
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [number, setNumber] = useState(query.get("number") || "");
  const [isShowReset, setIsShowReset] = useState(false);
  const [mercureStatus, setMercureStatus] = useState(
    /** @type {'connecting'|'open'|'reconnecting'|null} */ (null),
  );
  const [draggingIndices, setDraggingIndices] = useState(null);
  const [dropTargetIndex, setDropTargetIndex] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [voiceTestOpen, setVoiceTestOpen] = useState(false);
  const branchRef = useRef(null);
  const numberRef = useRef(number);
  const lastClickedIndexRef = useRef(null);

  numberRef.current = number;
  const isMercureConnectionPending = Boolean(getMercureHubUrl()) && mercureStatus !== "open";

  const getAndSetOrderPicking = useCallback(() => {
    setLoading(true);
    getOrderPicking(number ? { number } : {})
      .then((res) => {
        if (res.branch != null) setBranch(res.branch);
        setOrderPickingColumns({
          pending: res.pending ?? [],
          onAssembly: res.onAssembly ?? [],
          inProgress: res.inProgress ?? [],
        });
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, [number]);

  branchRef.current = branch;

  useEffect(() => {
    getAndSetOrderPicking();
  }, [getAndSetOrderPicking]);

  useEffect(() => {
    if (!getMercureHubUrl()) return undefined;

    const skipRelayIds = new Set();
    const onSkipRelay = (e) => {
      const rid = e?.detail?.relayId;
      if (rid) skipRelayIds.add(rid);
    };
    window.addEventListener(VOICE_ORDER_PICKING_SKIP_RELAY_EVENT, onSkipRelay);

    /**
     * Mercure вызывает onmessage синхронно, но async-обработчик не ждётся — несколько сообщений
     * шли параллельно и портил один общий voiceChunkArrays; плюс во время playAudioArrayBuffer
     * приходили новые чанки. Очередь + буфер на relayId устраняет гонки между устройствами/эфирами.
     */
    const voiceRelayKey = (relayId) => (relayId != null && relayId !== "" ? String(relayId) : "_");
    /** @type {Map<string, { chunks: Uint8Array[], format: string }>} */
    const voiceSessions = new Map();
    let voiceMercureChain = Promise.resolve();

    const applyVoiceMercureMessage = async (data) => {
      if (data.start === true) {
        const k = voiceRelayKey(data.relayId);
        voiceSessions.set(k, {
          chunks: [],
          format: typeof data.format === "string" && data.format ? data.format : "oggopus",
        });
        return;
      }
      if (typeof data.i !== "number") return;

      const k = voiceRelayKey(data.relayId);
      let sess = voiceSessions.get(k);
      if (!sess) {
        sess = { chunks: [], format: "oggopus" };
        voiceSessions.set(k, sess);
      }
      if (typeof data.format === "string" && data.format) {
        sess.format = data.format;
      }
      if (data.b64) {
        const bin = Uint8Array.from(atob(data.b64), (c) => c.charCodeAt(0));
        sess.chunks.push(bin);
      }
      if (data.last !== true) return;

      const rid = data.relayId != null && data.relayId !== "" ? String(data.relayId) : null;
      if (rid && skipRelayIds.has(rid)) {
        skipRelayIds.delete(rid);
        voiceSessions.delete(k);
        return;
      }
      const total = sess.chunks.reduce((s, a) => s + a.length, 0);
      voiceSessions.delete(k);
      if (total === 0) return;

      const merged = new Uint8Array(total);
      let off = 0;
      for (const a of sess.chunks) {
        merged.set(a, off);
        off += a.length;
      }
      await playAudioArrayBuffer(merged.buffer, mimeFromVoiceFormatForRelay(sess.format));
    };

    const M_TOPICS = [MERCURE_TOPIC_ORDER_PICKING, ORDER_PICKING_VOICE_MERCURE_TOPIC];

    const sub = subscribeMercureReconnect(M_TOPICS, {
      onStatus: setMercureStatus,
      onMessage: (event) => {
        try {
          const data = JSON.parse(event.data || "{}");

          const isVoiceMercure =
            data.start === true || (typeof data.i === "number" && !Number.isNaN(data.i));
          if (isVoiceMercure) {
            voiceMercureChain = voiceMercureChain
              .then(() => applyVoiceMercureMessage(data))
              .catch(() => {});
            return;
          }

          if (data.pending != null && data.onAssembly != null && data.inProgress != null) {
            const myBranch = branchRef.current;
            if (myBranch != null && data.branch === myBranch) {
              const filterNum = numberRef.current;
              const filter = (arr) =>
                !filterNum ? arr : arr.filter((item) => String(item.number) === String(filterNum));
              setOrderPickingColumns({
                pending: filter(data.pending ?? []),
                onAssembly: filter(data.onAssembly ?? []),
                inProgress: filter(data.inProgress ?? []),
              });
            }
          } else {
            getAndSetOrderPicking();
          }
        } catch {
          getAndSetOrderPicking();
        }
      },
    });

    return () => {
      sub.close();
      setMercureStatus(null);
      window.removeEventListener(VOICE_ORDER_PICKING_SKIP_RELAY_EVENT, onSkipRelay);
    };
  }, []);

  const isSearch = () => {
    if (
      number
    ) {
      return true;
    }
    return false;
  };

  const searchHandler = () => {
    if (!isSearch()) return;
    setLoading(true);
    getAndSetOrderPicking();
  };

  const handleKeypress = (e) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      searchHandler();
    }
  };

  useEffect(() => {
    setIsShowReset(isSearch());
  }, [number]);

  useEffect(() => {
    setSelectedIds((prev) => {
      const pendingIds = new Set(orderPickingColumns.pending.map((item) => item.id));
      const next = new Set([...prev].filter((id) => pendingIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [orderPickingColumns.pending]);


  const resetFilter = () => {
    setNumber("");
  };

  const toggleSelection = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectRange = useCallback((index, pending) => {
    const anchor = lastClickedIndexRef.current;
    if (anchor == null) {
      setSelectedIds(new Set([pending[index].id]));
      lastClickedIndexRef.current = index;
      return;
    }
    const from = Math.min(anchor, index);
    const to = Math.max(anchor, index);
    const ids = pending.slice(from, to + 1).map((item) => item.id);
    setSelectedIds(new Set(ids));
  }, []);

  const reorderPending = useCallback((fromIndices, toIndex) => {
    const indices = Array.isArray(fromIndices) ? [...fromIndices].sort((a, b) => a - b) : [fromIndices];
    setOrderPickingColumns((prev) => {
      const pending = [...prev.pending];
      const moved = indices.map((i) => pending[i]).filter(Boolean);
      indices.reverse().forEach((i) => pending.splice(i, 1));
      const insertIndex = Math.max(0, toIndex - indices.filter((i) => i < toIndex).length);
      moved.forEach((item, i) => pending.splice(insertIndex + i, 0, item));
      const onAssemblyIds = (prev.onAssembly ?? []).map((item) => item.id).filter(Boolean);
      const pendingIds = pending.map((item) => item.id);
      const orderIds = [...onAssemblyIds, ...pendingIds];
      orderPickingSort(orderIds).catch((e) => console.error("OrderPicking sort save failed:", e));
      return { ...prev, pending };
    });
  }, []);

  return (
    <Container fluid className="p-0 order-picking">
      {(loading || isMercureConnectionPending) && <MySpinner overlay />}

      <Card className="mb-0 filter">
        <CardBody>
          <div className="d-flex align-items-center flex-wrap mb-3">
            <h1 className="h3 mb-0 mr-3">Заявки на сборку</h1>
            <Button
              color="secondary"
              outline
              size="sm"
              className="mr-3 mb-1"
              type="button"
              onClick={() => setVoiceTestOpen(true)}
            >
              <FontAwesomeIcon icon={faVolumeUp} className="mr-2" />
              Тест голоса
            </Button>
            {getMercureHubUrl() && mercureStatus != null && (
              <div className="d-flex align-items-center small">
                <span
                  className={`order-picking__status-dot rounded-circle mr-2 ${
                    mercureStatus === "open" ? "bg-success" : mercureStatus === "reconnecting" ? "bg-warning" : "bg-secondary"
                  }`}
                  title={
                    mercureStatus === "open"
                      ? "SSE к Mercure активен"
                      : mercureStatus === "reconnecting"
                        ? "Соединение разорвано, выполняется переподключение"
                        : "Устанавливается соединение с Mercure"
                  }
                />
                {mercureStatus === "open" && "Real-time: подключено"}
                {mercureStatus === "connecting" && "Подключение к Mercure…"}
                {mercureStatus === "reconnecting" && "Переподключение к Mercure…"}
              </div>
            )}
          </div>

          <Form className="mb-3">
            <Row>
              <Col md={3}>
                <Label>Заказ №</Label>
                <Input
                  name="number"
                  placeholder="По номеру"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  onKeyDown={handleKeypress}
                  autocomplete="off"
                />
              </Col>
              <Col md={2}>
                <InputGroup className="with-label">
                  {isShowReset && (
                    <Button color="danger" onClick={() => resetFilter()}>
                      <FontAwesomeIcon icon={faWindowClose} />
                    </Button>
                  )}
                  <Button
                    color="primary"
                    onClick={searchHandler}
                    className="button-wide"
                  >
                    Поиск
                  </Button>
                </InputGroup>
              </Col>
            </Row>
          </Form>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="order-picking__wrapper">
          <div className="order-picking__columns">
            {COLUMN_KEYS.map((key, colIndex) => (
              <div
                key={key}
                className={`order-picking__column ${colIndex === 0 ? "order-picking__column--compact" : ""} ${colIndex === 1 ? "order-picking__column--barcode" : ""} ${colIndex === 2 ? "order-picking__column--in-progress" : ""}`}
                onDragLeave={(e) => {
                  if (colIndex === 0 && !e.currentTarget.contains(e.relatedTarget)) {
                    setDropTargetIndex(null);
                  }
                }}
              >
                <div className="order-picking__column-header">
                  <span className="order-picking__column-title">{COLUMN_TITLES[colIndex]}</span>
                  {colIndex === 0 && orderPickingColumns.pending.length > 0 && selectedIds.size > 0 && (
                    <div className="order-picking__selection-actions">
                      <Button
                        size="sm"
                        color="link"
                        className="p-0 order-picking__clear-selection-btn"
                        onClick={() => setSelectedIds(new Set())}
                      >
                        Снять выбор
                      </Button>
                      <span className="order-picking__selected-count">({selectedIds.size})</span>
                    </div>
                  )}
                </div>
                {orderPickingColumns[key].map((item, index) => (
                  <React.Fragment key={item.id}>
                    {colIndex === 0 && dropTargetIndex === index && draggingIndices !== null && !draggingIndices.includes(index) && (
                      <div
                        className="order-picking__drop-placeholder"
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "move";
                          setDropTargetIndex(index);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          let fromIndices = [];
                          try {
                            const data = JSON.parse(e.dataTransfer.getData("application/json") || "{}");
                            fromIndices = Array.isArray(data.indices) ? data.indices : [data.indices ?? 0];
                          } catch {
                            fromIndices = [parseInt(e.dataTransfer.getData("text/plain") || "0", 10)];
                          }
                          if (fromIndices.length && !fromIndices.includes(index)) {
                            reorderPending(fromIndices, index);
                          }
                          setDraggingIndices(null);
                          setDropTargetIndex(null);
                        }}
                      />
                    )}
                    <div
                      draggable={colIndex === 0}
                      onDragStart={(e) => {
                        const pending = orderPickingColumns.pending;
                        const indices = selectedIds.has(item.id)
                          ? pending.map((p, i) => (selectedIds.has(p.id) ? i : -1)).filter((i) => i >= 0)
                          : [index];
                        e.dataTransfer.setData("application/json", JSON.stringify({ indices }));
                        e.dataTransfer.setData("text/plain", String(index));
                        e.dataTransfer.effectAllowed = "move";
                        e.currentTarget.classList.add("order-picking__item--dragging");
                        setDraggingIndices(indices);
                      }}
                      onDragEnd={(e) => {
                        document.querySelectorAll(".order-picking__item--dragging").forEach((el) => el.classList.remove("order-picking__item--dragging"));
                        setDraggingIndices(null);
                        setDropTargetIndex(null);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        if (colIndex === 0 && draggingIndices !== null && !draggingIndices.includes(index)) {
                          setDropTargetIndex(index);
                        }
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        let fromIndices = [];
                        try {
                          const data = JSON.parse(e.dataTransfer.getData("application/json") || "{}");
                          fromIndices = Array.isArray(data.indices) ? data.indices : [data.indices ?? 0];
                        } catch {
                          fromIndices = [parseInt(e.dataTransfer.getData("text/plain") || "0", 10)];
                        }
                        if (colIndex === 0 && fromIndices.length && !fromIndices.includes(index)) {
                          reorderPending(fromIndices, index);
                        }
                        setDraggingIndices(null);
                        setDropTargetIndex(null);
                      }}
                      className={`${colIndex === 0 ? "order-picking__draggable" : ""} ${colIndex === 0 && selectedIds.has(item.id) ? "order-picking__item--selected" : ""} ${draggingIndices && draggingIndices.includes(index) ? "order-picking__item--dragging" : ""}`}
                      onClick={(e) => {
                        if (colIndex === 0 && !e.target.closest(".order-picking__number") && !e.target.closest(".order-picking__checkbox")) {
                          e.preventDefault();
                          if (e.shiftKey) {
                            selectRange(index, orderPickingColumns.pending);
                          } else if (e.ctrlKey || e.metaKey) {
                            toggleSelection(item.id);
                            lastClickedIndexRef.current = index;
                          } else {
                            setSelectedIds(new Set([item.id]));
                            lastClickedIndexRef.current = index;
                          }
                        }
                      }}
                    >
                      <div className="order-picking__item order-picking__item--full">
                        {colIndex === 0 && (
                          <>
                            <Input
                              type="checkbox"
                              className="order-picking__checkbox"
                              checked={selectedIds.has(item.id)}
                              onChange={() => toggleSelection(item.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div
                              className="order-picking__drag-handle"
                              onClick={(e) => e.preventDefault()}
                              onMouseDown={(e) => {
                                lastClickedIndexRef.current = orderPickingColumns.pending.findIndex((p) => p.id === item.id);
                              }}
                            >
                              <FontAwesomeIcon icon={faGripVertical} />
                            </div>
                          </>
                        )}
                        <div className="order-picking__number-block">
                          <Link to={`/sales/orders/${item.id}`} className="order-picking__number">
                            № {item.number}
                          </Link>
                          <div className="order-picking__customer">{item.customer}</div>
                        </div>
                        {colIndex === 1 ? (
                          <div className="order-picking__barcode">
                            <Barcode value={`O${item.number}`} format="CODE128" displayValue={false} height={60} width={2.3} />
                          </div>
                        ) : (
                          <div className="order-picking__icon">
                            <FontAwesomeIcon icon={faBox} />
                          </div>
                        )}
                        <div className="order-picking__route">
                          <div>
                            <span className="order-picking__route-label">Маршрут: </span>
                            <span className="order-picking__route-value">{item.route}</span>
                          </div>
                          {colIndex !== 0 && (
                            <div className="order-picking__delivery">
                              <span className="order-picking__route-label"></span>
                              <span className="order-picking__route-value">{item.delivery?.name}</span>
                            </div>
                          )}
                        </div>
                        <div className="order-picking__store-keeper">{item.storeKeeper}</div>
                        <div className="order-picking__started-at">
                          {item.startedAt &&
                            <>Начало сборки: <Moment format="HH:mm:ss">{item.startedAt}</Moment></>
                          }
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      <VoiceTestModal isOpen={voiceTestOpen} toggle={() => setVoiceTestOpen(false)} />
    </Container>
  );
};
